import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Message } from 'primereact/message';
import { Fieldset } from 'primereact/fieldset';
import { RadioButton } from 'primereact/radiobutton';
import { confirmDialog } from 'primereact/confirmdialog';
import { LdapManagedUser, LdapGroupInfo } from 'shared';
import { ldapUsersApi, ldapErrorMessage } from '../../api/ldapUsers';
import { GroupPicker } from './GroupPicker';

const editSchema = z.object({
  givenName: z.string().trim().min(1, 'First name is required').max(100),
  sn: z.string().trim().min(1, 'Last name is required').max(100),
  mail: z.string().trim().email('Invalid email address').max(254),
});

type EditFormData = z.infer<typeof editSchema>;

interface EditLdapUserDialogProps {
  user: LdapManagedUser | null;
  groups: LdapGroupInfo[];
  onHide: () => void;
  onSaved: () => void;
}

export const EditLdapUserDialog: React.FC<EditLdapUserDialogProps> = ({
  user,
  groups,
  onHide,
  onSaved,
}) => {
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Password reset panel state
  const [pwMode, setPwMode] = useState<'auto' | 'manual'>('auto');
  const [pwValue, setPwValue] = useState('');
  const [pwResetting, setPwResetting] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditFormData>({
    resolver: zodResolver(editSchema),
    defaultValues: { givenName: '', sn: '', mail: '' },
  });

  useEffect(() => {
    if (user) {
      reset({ givenName: user.givenName, sn: user.sn, mail: user.mail });
      setSelectedGroups([...user.groups]);
      setError('');
      setPwMode('auto');
      setPwValue('');
      setGeneratedPassword('');
      setPwSuccess(false);
    }
  }, [user, reset]);

  const handleSave = async (data: EditFormData) => {
    if (!user) return;
    try {
      setSubmitting(true);
      setError('');
      await ldapUsersApi.update(user.uid, { ...data, groups: selectedGroups });
      onSaved();
    } catch (err: any) {
      setError(ldapErrorMessage(err, 'Failed to update LDAP user'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = () => {
    if (!user) return;
    if (pwMode === 'manual' && pwValue.length < 12) return;
    confirmDialog({
      message: `Reset the password of ${user.uid}? The current password stops working immediately.`,
      header: 'Reset Password',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Reset',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          setPwResetting(true);
          setError('');
          const result = await ldapUsersApi.resetPassword(
            user.uid,
            pwMode === 'manual' ? pwValue : undefined
          );
          setGeneratedPassword(result.generatedPassword || '');
          setPwSuccess(true);
          setPwValue('');
        } catch (err: any) {
          setError(ldapErrorMessage(err, 'Failed to reset password'));
        } finally {
          setPwResetting(false);
        }
      },
    });
  };

  const copyPassword = async () => {
    await navigator.clipboard.writeText(generatedPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      data-component="ldap-edit-user-dialog"
      header={user ? `Edit LDAP User: ${user.uid}` : ''}
      visible={!!user}
      onHide={onHide}
      style={{ width: 'min(95vw, 700px)' }}
      modal
      footer={
        <div className="flex justify-end gap-2">
          <Button label="Cancel" className="p-button-text" onClick={onHide} disabled={submitting} />
          <Button
            label="Save"
            icon="pi pi-check"
            className="p-button-success"
            loading={submitting}
            onClick={handleSubmit(handleSave)}
          />
        </div>
      }
    >
      {user && (
        <div className="flex flex-col gap-4">
          {error && <Message severity="error" text={error} className="w-full" />}

          <div>
            <label className="text-sm font-medium block mb-1">Username (uid)</label>
            <InputText value={user.uid} disabled className="w-full font-mono" />
            <small className="text-gray-500">Usernames cannot be changed</small>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">First Name *</label>
              <Controller
                name="givenName"
                control={control}
                render={({ field }) => (
                  <InputText
                    value={field.value}
                    onChange={field.onChange}
                    className={`w-full ${errors.givenName ? 'p-invalid' : ''}`}
                  />
                )}
              />
              {errors.givenName && (
                <small className="text-red-500 mt-1 block">{errors.givenName.message}</small>
              )}
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Last Name *</label>
              <Controller
                name="sn"
                control={control}
                render={({ field }) => (
                  <InputText
                    value={field.value}
                    onChange={field.onChange}
                    className={`w-full ${errors.sn ? 'p-invalid' : ''}`}
                  />
                )}
              />
              {errors.sn && (
                <small className="text-red-500 mt-1 block">{errors.sn.message}</small>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Email *</label>
            <Controller
              name="mail"
              control={control}
              render={({ field }) => (
                <InputText
                  value={field.value}
                  onChange={field.onChange}
                  className={`w-full ${errors.mail ? 'p-invalid' : ''}`}
                />
              )}
            />
            {errors.mail && (
              <small className="text-red-500 mt-1 block">{errors.mail.message}</small>
            )}
          </div>

          <GroupPicker groups={groups} selected={selectedGroups} onChange={setSelectedGroups} />

          <Fieldset legend="Reset Password" toggleable collapsed data-component="ldap-reset-password">
            {pwSuccess ? (
              <div className="flex flex-col gap-2">
                {generatedPassword ? (
                  <>
                    <Message
                      severity="warn"
                      text="New password - shown only once, copy it now."
                      className="w-full"
                    />
                    <div className="flex items-center gap-2 border rounded-lg p-3 bg-gray-50">
                      <code className="font-mono flex-1 break-all select-all">
                        {generatedPassword}
                      </code>
                      <Button
                        icon={copied ? 'pi pi-check' : 'pi pi-copy'}
                        label={copied ? 'Copied' : 'Copy'}
                        className={copied ? 'p-button-success' : 'p-button-outlined'}
                        onClick={copyPassword}
                      />
                    </div>
                  </>
                ) : (
                  <Message severity="success" text="Password was reset." className="w-full" />
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <RadioButton
                    inputId="pw-auto"
                    checked={pwMode === 'auto'}
                    onChange={() => setPwMode('auto')}
                  />
                  <label htmlFor="pw-auto" className="text-sm">
                    Generate a strong password (shown once)
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioButton
                    inputId="pw-manual"
                    checked={pwMode === 'manual'}
                    onChange={() => setPwMode('manual')}
                  />
                  <label htmlFor="pw-manual" className="text-sm">
                    Set manually
                  </label>
                </div>
                {pwMode === 'manual' && (
                  <div>
                    <Password
                      value={pwValue}
                      onChange={(e) => setPwValue(e.target.value)}
                      toggleMask
                      feedback
                      className="w-full"
                      inputClassName="w-full"
                      placeholder="At least 12 characters"
                    />
                    {pwValue && pwValue.length < 12 && (
                      <small className="text-red-500 mt-1 block">
                        At least 12 characters required
                      </small>
                    )}
                  </div>
                )}
                <Button
                  label="Reset Password"
                  icon="pi pi-key"
                  className="p-button-outlined p-button-danger w-fit"
                  loading={pwResetting}
                  disabled={pwMode === 'manual' && pwValue.length < 12}
                  onClick={handleResetPassword}
                />
              </div>
            )}
          </Fieldset>
        </div>
      )}
    </Dialog>
  );
};
